import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import WhatsAppTextCarousel from '../components/utilities/WhatsappCarousel';


interface TextItem {
  text: string;
  link: string;
}


const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isIssuesDropdownOpen, setIsIssuesDropdownOpen] = useState(false);
  const [isServicesDropdownOpen, setIsServicesDropdownOpen] = useState(false);
  const [isAboutDropdownOpen, setIsAboutDropdownOpen] = useState(false);
  const [isPracticeDropdownOpen, setIsPracticeDropdownOpen] = useState(false);
  const [isCoursesDropdownOpen, setIsCoursesDropdownOpen] = useState(false);
  const [isSticky, setIsSticky] = useState<boolean>(false);
  const location = useLocation();

  // Timeout refs for each dropdown
  const issuesTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const servicesTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const aboutTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const practiceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const coursesTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Delay time in milliseconds
  const delayTime = 300;

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const closeMenu = () => {
    setIsMenuOpen(false);
    setIsIssuesDropdownOpen(false);
    setIsServicesDropdownOpen(false);
    setIsAboutDropdownOpen(false);
    setIsPracticeDropdownOpen(false);
    setIsCoursesDropdownOpen(false);
  };

  const issues = [
    { name: 'Complex Trauma and Grief', path: '/trauma' },
    { name: 'Relationship Concerns', path: '/relationship-concerns' },
    { name: 'Abuse', path: '/abuse' },
    { name: 'Gender-based Violence', path: '/gender-based-violence' },
    { name: 'Gender and Sexual Identity Concerns 🌈', path: '/gender-sexual-identity' }
  ];

  const services = [
    { name: 'Individual Therapy', path: '/services/individual-therapy' },
    { name: 'Couples Therapy', path: '/services/couples-therapy' },
    { name: 'Family Therapy', path: '/services/family-therapy' }
  ];

  const about = [
    { name: 'About Us', path: '/about' },
    { name: 'Our Team', path: '/our-team' },
  ];

  const practice = [
    { name: 'Group Practice', path: '/group-practice' },
    { name: 'Workshops & Training', path: '/training&workshops' },
  ];

  const courses = [
    { name: 'Trauma-Informed Therapy Certification', path: '/courses-at-grehpravesh-1' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsSticky(window.scrollY > 0);
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Helper functions for handling dropdown hover with delay
  const handleDropdownEnter = (
    setState: React.Dispatch<React.SetStateAction<boolean>>,
    timeoutRef: React.MutableRefObject<NodeJS.Timeout | null>
  ) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setState(true);
  };

  const handleDropdownLeave = (
    setState: React.Dispatch<React.SetStateAction<boolean>>,
    timeoutRef: React.MutableRefObject<NodeJS.Timeout | null>
  ) => {
    timeoutRef.current = setTimeout(() => {
      setState(false);
    }, delayTime);
  };

  const isActiveLink = (path: string) => {
    return location.pathname === path ? 'bg-white text-teal-900' : '';
  };

  const texts: TextItem[] = [
    { text: "🌟 Join our supportive WhatsApp community!", link: "  https://chat.whatsapp.com/LwFGWhGmGfX82mOky3pqBK" },
    { text: "💬 Connect with others on healing journeys, share experiences & receive support!", link: "  https://chat.whatsapp.com/LwFGWhGmGfX82mOky3pqBK" },
    { text: "🌟 Join our supportive WhatsApp community!", link: "  https://chat.whatsapp.com/LwFGWhGmGfX82mOky3pqBK" },
    { text: "💬 Connect with others on healing journeys, share experiences & receive support!", link: "  https://chat.whatsapp.com/LwFGWhGmGfX82mOky3pqBK" },


  ]

  return (
    <header className={`fixed top-0 left-0 z-[99] right-0 transition-all duration-300 ${isSticky ? 'bg-white shadow-md' : 'bg-transparent'}`}>
      <div className={`bg-white ${isSticky ? 'hidden' : 'block'}`}>
        <WhatsAppTextCarousel items={texts} speed={50} />
      </div>
      <div className="mx-auto w-full flex justify-between items-center px-2 sm:px-3 md:px-4 lg:px-6 py-2">
        {/* Logo Section */}
        <div className="flex items-center flex-shrink-0">
          <Link to="/" className="text-charcol flex flex-row items-center font-bold mr-2 md:mr-3 lg:mr-4">
            <img src='/logo.png' alt='logo' className='w-10 sm:w-12 md:w-12 lg:w-14 xl:w-16' />
            <h2 className={`gap-1 mx-2 md:mx-1 lg:mx-1 text-2xl lg:text-base xl:text-md ${isSticky ? 'text-teal-900' : 'text-white'}`}>
              Greh Pravesh Therapy
            </h2>
          </Link>
        </div>

        {/* Desktop Navigation */}
        <div className="hidden md:flex w-auto flex-grow items-center justify-between">
          <nav className="flex justify-center space-x-1 md:space-x-1 lg:space-x-1 xl:space-x-2 font-medium mx-2 md:mx-3 lg:mx-4">
            {/* Navigation links - adjusted spacing */}
            <Link to="/" className={`${isSticky ? 'text-teal-900' : 'text-charcol'} whitespace-nowrap hover:text-earthybeige transition-colors px-1 md:px-1.5 lg:px-2 py-1 md:py-1.5 rounded-md text-xs md:text-xs lg:text-lg ${isActiveLink('/')}`}>
              Home
            </Link>

            {/* About Dropdown with delay */}
            <div className="relative group"
              onMouseEnter={() => handleDropdownEnter(setIsAboutDropdownOpen, aboutTimeoutRef)}
              onMouseLeave={() => handleDropdownLeave(setIsAboutDropdownOpen, aboutTimeoutRef)}>
              <span className={`${isSticky ? 'text-teal-900' : 'text-charcol'} whitespace-nowrap hover:text-earthybeige transition-colors cursor-pointer flex items-center px-1 md:px-1.5 lg:px-2 py-1 md:py-1.5 rounded-md text-xs md:text-xs lg:text-md xl:text-lg`}>
                About
                <svg className="ml-0.5 w-2 md:w-2.5 h-2 md:h-2.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </span>
              {isAboutDropdownOpen && (
                <div
                  className="absolute left-0 mt-1 w-36 md:w-40 bg-white shadow-lg rounded-md py-1"
                  onMouseEnter={() => handleDropdownEnter(setIsAboutDropdownOpen, aboutTimeoutRef)}
                  onMouseLeave={() => handleDropdownLeave(setIsAboutDropdownOpen, aboutTimeoutRef)}>
                  {about.map((item) => (
                    <Link key={item.path} to={item.path} className={`block px-2 py-1.5 text-charcol hover:bg-sage/20 text-xs md:text-sm ${isActiveLink(item.path)}`} onClick={() => setIsAboutDropdownOpen(false)}>
                      {item.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Issues Dropdown with delay */}
            <div className="relative group"
              onMouseEnter={() => handleDropdownEnter(setIsIssuesDropdownOpen, issuesTimeoutRef)}
              onMouseLeave={() => handleDropdownLeave(setIsIssuesDropdownOpen, issuesTimeoutRef)}>
              <span className={`${isSticky ? 'text-teal-900' : 'text-charcol'} whitespace-nowrap hover:text-earthybeige transition-colors cursor-pointer flex items-center px-1 md:px-1.5 lg:px-2 py-1 md:py-1.5 rounded-md text-xs md:text-xs lg:text-md xl:text-lg`}>
                Issues
                <svg className="ml-0.5 w-2 md:w-2.5 h-2 md:h-2.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </span>
              {isIssuesDropdownOpen && (
                <div
                  className="absolute left-0 mt-1 w-40 md:w-44 bg-white shadow-lg rounded-md py-1"
                  onMouseEnter={() => handleDropdownEnter(setIsIssuesDropdownOpen, issuesTimeoutRef)}
                  onMouseLeave={() => handleDropdownLeave(setIsIssuesDropdownOpen, issuesTimeoutRef)}>
                  {issues.map((issue) => (
                    <Link key={issue.path} to={issue.path} className={`block px-2 py-1.5 text-charcol hover:bg-sage/20 text-xs md:text-sm ${isActiveLink(issue.path)}`} onClick={() => setIsIssuesDropdownOpen(false)}>
                      {issue.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Services Dropdown with delay */}
            <div className="relative group"
              onMouseEnter={() => handleDropdownEnter(setIsServicesDropdownOpen, servicesTimeoutRef)}
              onMouseLeave={() => handleDropdownLeave(setIsServicesDropdownOpen, servicesTimeoutRef)}>
              <span className={`${isSticky ? 'text-teal-900' : 'text-charcol'} whitespace-nowrap hover:text-earthybeige transition-colors cursor-pointer flex items-center px-1 md:px-1.5 lg:px-2 py-1 md:py-1.5 rounded-md text-xs md:text-xs lg:text-md xl:text-lg`}>
                Services
                <svg className="ml-0.5 w-2 md:w-2.5 h-2 md:h-2.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </span>
              {isServicesDropdownOpen && (
                <div
                  className="absolute left-0 mt-1 w-36 md:w-40 bg-white shadow-lg rounded-md py-1"
                  onMouseEnter={() => handleDropdownEnter(setIsServicesDropdownOpen, servicesTimeoutRef)}
                  onMouseLeave={() => handleDropdownLeave(setIsServicesDropdownOpen, servicesTimeoutRef)}>
                  {services.map((service) => (
                    <Link key={service.path} to={service.path} className={`block px-2 py-1.5 text-charcol hover:bg-sage/20 text-xs md:text-sm ${isActiveLink(service.path)}`} onClick={() => setIsServicesDropdownOpen(false)}>
                      {service.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Practice & Training Dropdown with delay */}
            <div className="relative group"
              onMouseEnter={() => handleDropdownEnter(setIsPracticeDropdownOpen, practiceTimeoutRef)}
              onMouseLeave={() => handleDropdownLeave(setIsPracticeDropdownOpen, practiceTimeoutRef)}>
              <span className={`${isSticky ? 'text-teal-900' : 'text-charcol'} whitespace-nowrap hover:text-earthybeige transition-colors cursor-pointer flex items-center px-1 md:px-1.5 lg:px-2 py-1 md:py-1.5 rounded-md text-xs md:text-xs lg:text-md xl:text-lg`}>
                Practice & Training
                <svg className="ml-0.5 w-2 md:w-2.5 h-2 md:h-2.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </span>
              {isPracticeDropdownOpen && (
                <div
                  className="absolute left-0 mt-1 w-40 md:w-44 bg-white shadow-lg rounded-md py-1"
                  onMouseEnter={() => handleDropdownEnter(setIsPracticeDropdownOpen, practiceTimeoutRef)}
                  onMouseLeave={() => handleDropdownLeave(setIsPracticeDropdownOpen, practiceTimeoutRef)}>
                  {practice.map((item) => (
                    <Link key={item.path} to={item.path} className={`block px-2 py-1.5 text-charcol hover:bg-sage/20 text-xs md:text-sm ${isActiveLink(item.path)}`} onClick={() => setIsPracticeDropdownOpen(false)}>
                      {item.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Courses Dropdown with delay */}
            <div className="relative group"
              onMouseEnter={() => handleDropdownEnter(setIsCoursesDropdownOpen, coursesTimeoutRef)}
              onMouseLeave={() => handleDropdownLeave(setIsCoursesDropdownOpen, coursesTimeoutRef)}>
              <span className={`${isSticky ? 'text-teal-900' : 'text-charcol'} whitespace-nowrap hover:text-earthybeige transition-colors cursor-pointer flex items-center px-1 md:px-1.5 lg:px-2 py-1 md:py-1.5 rounded-md text-xs md:text-xs lg:text-md xl:text-lg`}>
                Courses
                <svg className="ml-0.5 w-2 md:w-2.5 h-2 md:h-2.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </span>
              {isCoursesDropdownOpen && (
                <div
                  className="absolute left-0 mt-1 w-52 md:w-56 bg-white shadow-lg rounded-md py-1"
                  onMouseEnter={() => handleDropdownEnter(setIsCoursesDropdownOpen, coursesTimeoutRef)}
                  onMouseLeave={() => handleDropdownLeave(setIsCoursesDropdownOpen, coursesTimeoutRef)}>
                  {courses.map((course) => (
                    <Link key={course.path} to={course.path} className={`block px-2 py-1.5 text-charcol hover:bg-sage/20 text-xs md:text-sm ${isActiveLink(course.path)}`} onClick={() => setIsCoursesDropdownOpen(false)}>
                      {course.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link to="/blogs" className={`${isSticky ? 'text-teal-900' : 'text-charcol'} whitespace-nowrap hover:text-earthybeige transition-colors px-1 md:px-1.5 lg:px-2 py-1 md:py-1.5 rounded-md text-xs md:text-xs lg:text-md xl:text-lg ${isActiveLink('/blogs')}`}>
              Blogs
            </Link>

            <Link to="/contact" className={`${isSticky ? 'text-teal-900' : 'text-charcol'} whitespace-nowrap hover:text-earthybeige transition-colors px-1 md:px-1.5 lg:px-2 py-1 md:py-1.5 rounded-md text-xs md:text-xs lg:text-md xl:text-lg ${isActiveLink('/contact')}`}>
              Contact
            </Link>
          </nav>

          {/* CTA Section */}
          <div className="flex items-center space-x-2 md:space-x-3 lg:space-x-4">
            <Link to="/join-us" className="bg-sage text-white px-3 md:px-4 lg:px-6 py-1 md:py-1.5 lg:py-2 rounded-md hover:bg-sage/90 transition-colors font-semibold text-xs md:text-md lg:text-md shadow-lg whitespace-nowrap">
              Join Us
            </Link>
            <Link to="/book-appointment" className="bg-orange-400 text-white px-3 md:px-4 lg:px-6 py-1 md:py-1.5 lg:py-2 rounded-md hover:bg-palegola/90 transition-colors font-semibold text-xs md:text-md lg:text-md shadow-lg whitespace-nowrap">
              Book Now
            </Link>
          </div>
        </div>

        {/* Mobile Menu Button */}
        <div className="md:hidden fixed right-4 z-[1000]">
          <button
            onClick={toggleMenu}
            className="text-charcol focus:outline-none relative w-6 h-6"
          >
            {isMenuOpen ? (
              <div className="w-full  top-2">
                <div className="w-full h-[3px] bg-charcol transform rotate-45 origin-center absolute top-1/2 -translate-y-1/2"></div>
                <div className="w-full h-[3px] bg-charcol transform -rotate-45 origin-center absolute top-1/2 -translate-y-1/2"></div>
              </div>
            ) : (
              <div className="flex flex-col top-2 justify-between h-6">
                <div className={`${isSticky ? 'bg-teal-700' : 'bg-white'} w-3/4 h-[3px] self-start`}></div>
                <div className={`${isSticky ? 'bg-teal-700' : 'bg-white'} w-4/5 h-[3px] self-start`}></div>
                <div className={`${isSticky ? 'bg-teal-700' : 'bg-white'} w-full h-[3px]`}></div>
              </div>
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu - Right Side Sliding Navigation */}
      <div className={`
        md:hidden 
        fixed 
        top-0 
        right-0 
        w-64 
        h-full 
        bg-white 
        shadow-lg 
        transform 
        transition-transform 
        duration-300 
        ease-in-out 
        ${isMenuOpen ? 'translate-x-0' : 'translate-x-full'}
        z-[999] 
        pt-20 
        overflow-y-auto
      `}>
        <nav className="flex flex-col p-6 space-y-4">

          <Link
            to="/"
            className={`text-xl text-charcol hover:text-earthybeige ${isActiveLink('/')}`}
            onClick={closeMenu}
          >
            Home
          </Link>
          <Link
            to="/about"
            className={`text-xl text-charcol hover:text-earthybeige ${isActiveLink('/about')}`}
            onClick={closeMenu}
          >
            About Us
          </Link>

          {/* Mobile Issues Submenu */}
          <div>
            <div
              className="text-xl text-charcol hover:text-earthybeige flex justify-between items-center"
              onClick={() => setIsIssuesDropdownOpen(!isIssuesDropdownOpen)}
            >
              Issues
              <svg className={`w-5 h-5 transform transition-transform ${isIssuesDropdownOpen ? 'rotate-180' : ''}`} fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </div>
            {isIssuesDropdownOpen && (
              <div className="ml-4 mt-2 space-y-2">
                {issues.map((issue) => (
                  <Link
                    key={issue.path}
                    to={issue.path}
                    className={`block text-charcol hover:text-earthybeige ${isActiveLink(issue.path)}`}
                    onClick={closeMenu}
                  >
                    {issue.name}
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Services Submenu */}
          <div>
            <div
              className="text-xl text-charcol hover:text-earthybeige flex justify-between items-center"
              onClick={() => setIsServicesDropdownOpen(!isServicesDropdownOpen)}
            >
              Services
              <svg className={`w-5 h-5 transform transition-transform ${isServicesDropdownOpen ? 'rotate-180' : ''}`} fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </div>
            {isServicesDropdownOpen && (
              <div className="ml-4 mt-2 space-y-2">
                {services.map((service) => (
                  <Link
                    key={service.path}
                    to={service.path}
                    className={`block text-charcol hover:text-earthybeige ${isActiveLink(service.path)}`}
                    onClick={closeMenu}
                  >
                    {service.name}
                  </Link>
                ))}
              </div>
            )}
          </div>

          <Link
            to="/our-team"
            className={`text-xl text-charcol hover:text-earthybeige ${isActiveLink('/our-team')}`}
            onClick={closeMenu}
          >
            Our Team
          </Link>

          <Link
            to="/group-practice"
            className={`text-xl text-charcol hover:text-earthybeige ${isActiveLink('/group-practice')}`}
            onClick={closeMenu}
          >
            Group Practice
          </Link>

          <Link
            to="/training&workshops"
            className={`text-xl text-charcol hover:text-earthybeige ${isActiveLink('/training&workshops')}`}
            onClick={closeMenu}
          >
            Training & Workshops
          </Link>

          {/* Mobile Courses Submenu */}
          <div>
            <div
              className="text-xl text-charcol hover:text-earthybeige flex justify-between items-center"
              onClick={() => setIsCoursesDropdownOpen(!isCoursesDropdownOpen)}
            >
              Courses
              <svg className={`w-5 h-5 transform transition-transform ${isCoursesDropdownOpen ? 'rotate-180' : ''}`} fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </div>
            {isCoursesDropdownOpen && (
              <div className="ml-4 mt-2 space-y-2">
                {courses.map((course) => (
                  <Link
                    key={course.path}
                    to={course.path}
                    className={`block text-charcol hover:text-earthybeige ${isActiveLink(course.path)}`}
                    onClick={closeMenu}
                  >
                    {course.name}
                  </Link>
                ))}
              </div>
            )}
          </div>

          <Link
            to="/blogs"
            className={`text-xl text-charcol hover:text-earthybeige ${isActiveLink('/blogs')}`}
            onClick={closeMenu}
          >
            Blogs
          </Link>
          <Link
            to="/contact"
            className={`text-xl text-charcol hover:text-earthybeige ${isActiveLink('/contact')}`}
            onClick={closeMenu}
          >
            Contact Us
          </Link>

          {/* Mobile CTA */}
          <div className="mt-6 space-y-3">
            <Link to="/join-us" className="block bg-sage text-white px-6 py-2.5 rounded-md text-center hover:bg-sage/90 transition-colors font-semibold" onClick={closeMenu}>
              Join Us
            </Link>
            <Link to="/book-appointment" className="block bg-orange-400 text-white px-6 py-2.5 rounded-md text-center hover:bg-palegola/90 transition-colors font-semibold" onClick={closeMenu}>
              Book Now
            </Link>
          </div>
        </nav>
      </div>
    </header>
  );
};

export default Header;
